import Vue from "vue";
import { WHITELABEL_PLATFORM_DEFAULT } from "@packages/util/whitelabel/whitelabelDefaults";
import { persistWhitelabelKey } from "@packages/util/whitelabel/whitelabelKey";
import { sanitizeWhitelabelStyle } from "@packages/util/whitelabel/whitelabelStyle";
import { sanitizeSupportMode } from "@packages/util/whitelabel/whitelabelSupport";

const EMPTY_SUPPORT = { mode: null, email: "" };

function buildSupport(whitelabel) {
    return {
        mode: sanitizeSupportMode(whitelabel.supportMode),
        email: (whitelabel.supportEmail || "").trim(),
    };
}

function isDefaultAsset(url) {
    if (!url) return true;

    const segment = url.split("/").pop() || "";
    const nameWithoutExt = segment.replace(/\.[^./]+$/, "");

    return nameWithoutExt === "default" || nameWithoutExt.endsWith("-default");
}

function isDifferentValue(value, defaultValue) {
    if (typeof value !== "string" || !value) return false;

    return value.toLowerCase() !== defaultValue.toLowerCase();
}

function hasCustomStyle(style) {
    if (!style) return false;

    const defaults = WHITELABEL_PLATFORM_DEFAULT.style;
    const charts = style.charts || [];

    return (
        isDifferentValue(style.font, defaults.font) ||
        isDifferentValue(style.primary, defaults.primary) ||
        isDifferentValue(style.accent, defaults.accent) ||
        isDifferentValue(style.background, defaults.background) ||
        defaults.charts.some((color, index) => isDifferentValue(charts[index], color))
    );
}

function hasCustomIdentity(identity) {
    return (
        !!identity.logoUrl ||
        !!identity.faviconUrl ||
        isDifferentValue(identity.title, WHITELABEL_PLATFORM_DEFAULT.title) ||
        hasCustomStyle(identity.style)
    );
}

function buildIdentity(whitelabel) {
    return {
        title: whitelabel.title || "",
        logoUrl: isDefaultAsset(whitelabel.logoUrl) ? "" : whitelabel.logoUrl,
        faviconUrl: isDefaultAsset(whitelabel.faviconUrl) ? "" : whitelabel.faviconUrl,
        style: sanitizeWhitelabelStyle(whitelabel.style || {}),
    };
}

function fetchWhitelabel(key, isAuthenticated) {
    if (!isAuthenticated) return Vue.api.auth.getPublicWhitelabel({ key });

    return Vue.api.auth
        .getAuthenticatedWhitelabel()
        .catch(() => Vue.api.auth.getPublicWhitelabel({ key }));
}

export default {
    namespaced: true,

    state: {
        identity: null,
        support: { ...EMPTY_SUPPORT },
    },

    getters: {
        isApplied: (state) => !!state.identity,
        style: (state) => state.identity && state.identity.style,
        title: (state) => (state.identity ? state.identity.title : ""),
        logoUrl: (state) => (state.identity ? state.identity.logoUrl : ""),
        faviconUrl: (state) => (state.identity ? state.identity.faviconUrl : ""),
        chartColors: (state) => (state.identity ? state.identity.style.charts : null),
        supportMode: (state) => state.support.mode,
        supportEmail: (state) => state.support.email,
    },

    mutations: {
        SET_IDENTITY(state, payload) {
            state.identity = payload;
        },

        SET_SUPPORT(state, payload) {
            state.support = payload;
        },
    },

    actions: {
        async load({ commit }, { key, isAuthenticated } = {}) {
            try {
                const whitelabel = await fetchWhitelabel(key, isAuthenticated);

                commit("SET_SUPPORT", whitelabel ? buildSupport(whitelabel) : { ...EMPTY_SUPPORT });

                const identity = whitelabel ? buildIdentity(whitelabel) : null;

                if (!identity || !hasCustomIdentity(identity)) {
                    commit("SET_IDENTITY", null);
                    return;
                }

                persistWhitelabelKey(key);

                commit("SET_IDENTITY", identity);
            } catch (e) {
                commit("SET_IDENTITY", null);
                commit("SET_SUPPORT", { ...EMPTY_SUPPORT });
            }
        },

        clear({ commit }) {
            commit("SET_IDENTITY", null);
            commit("SET_SUPPORT", { ...EMPTY_SUPPORT });
        },
    },
};
